// src/features/auth/store/authStore.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';


import type { MenuItem } from '../types/menuTypes';
import type { Permissions } from '../types/authTypes';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  menus: MenuItem[] | null;
  permissions: Permissions | null;
  name: string | null;
  userType: string | null;
  hasHydrated: boolean;
  setToken: (token: string) => void;
  clearToken: () => void;
  setRefreshToken: (refreshToken: string) => void;
  clearRefreshToken: () => void;
  setMenus: (menus: MenuItem[]) => void;
  clearMenus: () => void;
  setPermissions: (permissions: Permissions) => void;
  clearPermissions: () => void;
  setAuthData: (token: string, refreshToken: string, menus: MenuItem[], permissions: Permissions, name: string, userType: string) => void;
  isAuthenticated: () => boolean;
  setName: (name: string) => void;
  setUserType: (userType: string) => void;
  clearName: () => void;
  setHasHydrated: (value: boolean) => void;
  logout: (options?: { broadcast?: boolean }) => void; // Add logout method for cross-tab
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      menus: null,
      permissions: null,
      name: null, // <-- Add this line
      userType: null,
      hasHydrated: false,
      setToken: (token: string) => {
        set({ token });
        // Broadcast login event
        localStorage.setItem('insurance_auth_event', JSON.stringify({ type: 'login', timestamp: Date.now() }));
        if ('BroadcastChannel' in window) {
          const channel = new BroadcastChannel('auth');
          channel.postMessage({ type: 'login' });
          channel.close();
        }
      },
      clearToken: () => {
        set({ token: null });
        // Broadcast logout event
        localStorage.setItem('insurance_auth_event', JSON.stringify({ type: 'logout', timestamp: Date.now() }));
        if ('BroadcastChannel' in window) {
          const channel = new BroadcastChannel('auth');
          channel.postMessage({ type: 'logout' });
          channel.close();
        }
      },
      setRefreshToken: (refreshToken: string) => set({ refreshToken }),
      clearRefreshToken: () => set({ refreshToken: null }),
      setMenus: (menus: MenuItem[]) => set({ menus }),
      clearMenus: () => set({ menus: null }),
      setPermissions: (permissions: Permissions) => set({ permissions }),
      clearPermissions: () => set({ permissions: null }),
      setName: (name: string) => set({ name }), // <-- Add this line
      setUserType: (userType: string) => set({ userType }),
      clearName: () => set({ name: null }), // <-- Add this line
      setHasHydrated: (value: boolean) => set({ hasHydrated: value }),
      setAuthData: (token: string, refreshToken: string, menus: MenuItem[], permissions: Permissions, name: string, userType: string) => {
        set({ token, refreshToken, menus, permissions, name, userType });
        // Broadcast login event
        localStorage.setItem('insurance_auth_event', JSON.stringify({ type: 'login', timestamp: Date.now() }));
        if ('BroadcastChannel' in window) {
          const channel = new BroadcastChannel('auth');
          channel.postMessage({ type: 'login' });
          channel.close();
        }
      },
      logout: (options) => {
        set({ token: null, refreshToken: null, menus: null, permissions: null, name: null, userType: null });
        localStorage.removeItem('insurance-auth-storage');
        const shouldBroadcast = options?.broadcast !== false;
        if (shouldBroadcast) {
          // Broadcast logout event
          localStorage.setItem('insurance_auth_event', JSON.stringify({ type: 'logout', timestamp: Date.now() }));
          if ('BroadcastChannel' in window) {
            const channel = new BroadcastChannel('auth');
            channel.postMessage({ type: 'logout' });
            channel.close();
          }
        }
      },
      isAuthenticated: () => !!get().token,
    }),
    {
      name: 'insurance-auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        menus: state.menus,
        permissions: state.permissions,
        name: state.name, // <-- Add this line
        userType: state.userType
      }),
    }
  )
);